import { AsyncButton, loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { TlfContext } from "contexts/TlfContext";
import { eCommentReviewStatus } from "functions/api/comments/interface";
import {
  PatchCommentThreadDetails,
  patchCommentThreadStatus,
} from "functions/api/comments/patchCommentThreadStatus";
import { eAccessRights } from "functions/api/enums";
import { IRequestStatus } from "functions/api/interface";
import { MouseEventHandler, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Check } from "react-bootstrap-icons";
import { CommentViewContext } from "./interface";
import { useComments } from "./useComments";

interface ResolveThreadCommentsButtonProps {
  activeContext: CommentViewContext;
  commentId: string;
}

/**
 * Resolve an entire comment thread and its replies.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 * @param props.commentId - Id of the thread's root comment.
 */
export const ResolveThreadCommentsButton = ({
  activeContext,
  commentId,
}: ResolveThreadCommentsButtonProps): ReactNode => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchy = hierarchyContext?.hierarchy;
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;

  const { comments, readComment, setComments } = useComments(activeContext);

  const thisComment = readComment(commentId);

  /**
   * Check if a comment thread has any unresolved (open) comments.
   * @param parentCommentId - Id of the root comment for the recursive check.
   * @returns True if any comment in the thread is open, false otherwise.
   */
  const isCommentThreadOpen = useCallback(
    (parentCommentId: string): boolean => {
      /**
       * Recursive check for any open child comments.
       *
       * @param commentId - Id of the root comment for the recursive check.
       */
      const hasOpenChildComments = (commentId: string): boolean => {
        const currentComment = comments.find((comment) => comment.id === commentId);
        if (
          currentComment &&
          (!currentComment.commentReviewStatus || currentComment.commentReviewStatus === "Open")
        ) {
          return true;
        }

        const childComments = comments.filter((comment) => comment.parentId === commentId);

        return childComments.some((childComment) => hasOpenChildComments(childComment.id));
      };

      return hasOpenChildComments(parentCommentId);
    },
    [comments],
  );

  const thisCommentRequestDto: PatchCommentThreadDetails | null = useMemo(() => {
    if (activeContext === "Hierarchy" && hierarchy) {
      return {
        activeContext: "Hierarchy",
        hierarchyId: hierarchy.id,
        commentId: commentId,
        newStatus: eCommentReviewStatus.resolved,
      };
    } else if (activeContext === "TlfItem" && tlfData) {
      return {
        activeContext: "TlfItem",
        id: tlfData.id,
        azsolId: tlfData.azsolId,
        version: tlfData.version,
        commentId: commentId,
        newStatus: eCommentReviewStatus.resolved,
      };
    }
    return null;
  }, [activeContext, commentId, hierarchy, tlfData]);

  const [patchCommentStatusRequest, setPatchCommentStatusRequest] = useState<
    IRequestStatus<PatchCommentThreadDetails>
  >({
    requesting: false,
    error: false,
  });

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      /* istanbul ignore else */
      if (thisCommentRequestDto) {
        const request = await loadWrapper(
          patchCommentThreadStatus,
          thisCommentRequestDto,
          { ...patchCommentStatusRequest, requestedId: undefined, error: false },
          setPatchCommentStatusRequest,
          appSettings.backEndUrl,
        );
        /* istanbul ignore else */
        if (request?.success) {
          const newComments = [
            ...comments.filter((c) => !request.data.map((c) => c.id).includes(c.id)),
            ...request.data,
          ];
          setComments(newComments, true);
        }
      }
    },
    [
      appSettings.backEndUrl,
      comments,
      patchCommentStatusRequest,
      setComments,
      thisCommentRequestDto,
    ],
  );

  return (
    thisComment && (
      <AsyncButton
        id={`resolve-thread-comments-${commentId}`}
        className="resolve-thread-button mb-1"
        title="Resolve thread"
        size="sm"
        variant="success"
        disabled={
          !hierarchyContext?.accessRights.includes(eAccessRights.MaintainHierarchyComments) ||
          !isCommentThreadOpen(commentId) ||
          commentId.startsWith("new-")
        }
        requestStatus={patchCommentStatusRequest}
        onClick={handleClick}
      >
        <Check className="me-1" />
        Resolve thread
      </AsyncButton>
    )
  );
};

ResolveThreadCommentsButton.displayName = "ResolveThreadCommentsButton";
